import React, {FC, useContext} from 'react';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {PushContext} from '../../../../srcCommon/push/pushContext';
import {buildTeammateAssignedCounterKey, unifiedTeammatesCounterKey} from '../../../helpers/counterHelpers';
import {buildGlobalPath, buildTeammatePath} from '../../../helpers/modelHelpers';
import {TeammateLicenseTypesEnum, TeammateModel} from '../../../models/api/teammateModel';
import {
  GlobalFoldersEnum,
  InboxesFiltersEnum,
  TeammatesFoldersEnum,
} from '../../../models/url/models/inboxesUrlModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../services/tracking';
import {InboxesPanelId} from '../../../store/panels/panelStateBase';
import {WorkspaceDragSourceTypesEnum} from './dnd/workspaceDragDropTypes';
import {WorkspaceHeader} from './header/workspaceHeader';
import {Workspace} from './workspace';
import {WorkspaceGenericItemContainer} from './workspaceGenericItemContainer';
import {WorkspaceTeammateItemContainer} from './workspaceTeammateItemContainer';

/*
 * Props.
 */

type FindSelectedFilter = (teammate: TeammateModel) => InboxesFiltersEnum;

export interface TeammatesWorkspaceProps {
  panelId: InboxesPanelId;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpandClick: Handler;
  /** Current filter for the unified view. */
  globalAssignedFilter: InboxesFiltersEnum;
  /** Current user license (full). */
  userLicenseType: TeammateLicenseTypesEnum;
  /** Whether this user is a global admin. */
  isUserGlobalAdmin: boolean;
  /** The list of teammates to display in this workspace. */
  teammates: ReadonlyArray<TeammateModel>;
  /** Find the selected filter of the provided teammate. */
  findSelectedFilter: FindSelectedFilter;
  shouldAlphabetizeWorkspace: boolean;
  /** Event handler to hide the teammates workspace section. */
  onHideSection: Handler;
}

/*
 * Component.
 */

/** Displays the workspace of teammates' assigned conversations. */
export const TeammatesWorkspace: FC<TeammatesWorkspaceProps> = (props) => {
  const {isConnected} = useContext(PushContext);
  const {panelId, isExpanded, userLicenseType, onHideSection} = props;

  // Hide the workspace completely for non-full teammates.
  if (userLicenseType !== TeammateLicenseTypesEnum.FULL) {
    return null;
  }

  const onToggleExpandClick = () => {
    props.onToggleExpandClick();
  };

  const isOffline = !isConnected;

  return (
    <Workspace isExpanded={isExpanded}>
      {/* Header. */}
      <WorkspaceHeader
        panelId={panelId}
        interactionId="teammates"
        isExpanded={isExpanded}
        onToggleExpandClick={onToggleExpandClick}
        isOffline={isOffline}
        onHideSection={onHideSection}
      >
        <Trans id="workspace-assigned-title">Assigned conversations</Trans>
      </WorkspaceHeader>

      {/* Workspace content. */}
      {maybeRenderExpandedContent(props, isOffline)}
    </Workspace>
  );
};

function trackTeammateClick(teammate: TeammateModel) {
  trackInterfaceEvent(InterfaceEventsEnum.CLICK_TEAMMATE_P1, {teammate_id: teammate.id});
}

function maybeRenderExpandedContent(props: TeammatesWorkspaceProps, isOffline: boolean) {
  const {panelId, isExpanded, shouldAlphabetizeWorkspace} = props;

  if (!isExpanded) {
    return null;
  }

  const lastIndex = props.teammates.length - 1;

  return (
    <>
      {/* Aggregate view. */}
      <WorkspaceGenericItemContainer
        shouldAlphabetizeWorkspace={shouldAlphabetizeWorkspace}
        trackingSection={WorkspaceDisplaySectionTrackingTypesEnum.ALL_TEAMMATES_VIEW}
        canBeHidden={false}
        panelId={panelId}
        iconName="teammatesSmall"
        selectedIconName="teammatesFilledSmall"
        to={buildGlobalPath(GlobalFoldersEnum.ASSIGNED, props.globalAssignedFilter)}
        counterKey={unifiedTeammatesCounterKey}
        isDisabled={isOffline}
        workspaceDragItem={undefined}
        shouldHideMoreMenu
      >
        <Trans id="workspace-assigned">All teammates</Trans>
      </WorkspaceGenericItemContainer>

      {/* Teammates. */}
      {props.teammates.map((t, index) => (
        <WorkspaceTeammateItemContainer
          key={t.id}
          panelId={panelId}
          teammate={t}
          to={buildTeammatePath(t.id, TeammatesFoldersEnum.ASSIGNED_SHARED, props.findSelectedFilter(t))}
          onClick={() => trackTeammateClick(t)}
          counterKey={buildTeammateAssignedCounterKey(t.id)}
          isDisabled={isOffline}
          workspaceDragItem={{
            type: WorkspaceDragSourceTypesEnum.ASSIGNED_TEAMMATE,
            id: t.id,
            isLast: index === lastIndex,
          }}
          shouldAlphabetizeWorkspace={shouldAlphabetizeWorkspace}
        />
      ))}
    </>
  );
}
